import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { barHorizontal } from 'libs/biReportGraphs/index.js';
import { bubbles } from 'libs/biReportGraphs/index.js';
import { barVertical } from 'libs/biReportGraphs/index.js';
import 'libs/biReportGraphs/src/index.css';
import { saveSvgAsPng } from 'save-svg-as-png';
import { ensure } from 'components/sharpr/ensure/ensure.js';
import { queryString } from 'components/sharpr/url/builder/urlBuilder.js';
import { AuthTypes } from 'components/sharpr/auth/AuthTypes.js';
import moment from 'moment';
import _ from 'lodash';

export function ReportViewerRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.ReportViewer', {
		url: '/analytics/reports/:name?color&user_domain&user_domain_treatment&end&filter_text&graph&hubs&limit&roles&start&trend_label&trend_of&excluded_values&in_iframe&divisions',
		controllerAs: 'vm',
		controller: ReportViewerCtrl,
		templateUrl: '/ng/hub/Report/Viewer/ReportViewerCtrl.html?' + window.BUILD,
		resolve: {
			report: ApiResourcify.endpoint('/analytics/reports/:name', {
				cacheFor: 30 * 60 * 1000,
			}),
			style: () => AssetManager.css('/ng/hub/Report/Viewer/ReportViewerCtrl.css'),
			$title: () => 'Analytics Report',
		},
	});
}

export class ReportViewerCtrl {
	constructor(report, modal, $stateParams, $timeout, $location, Api, Identity, Theme, Att, storage, FineTunePosts, Cfg) {
		'ngInject';
		// save injections
		this.report = report;
		this.modal = modal;
		this.$timeout = $timeout;
		this.$location = $location;
		this.$stateParams = $stateParams;
		this.Api = Api;
		this.Theme = Theme;
		this.Att = Att;
		this.storage = storage;
		this.FineTunePosts = FineTunePosts;
		this.Cfg = Cfg;
		this.user_domain = '';
		this.domainsOpen = false;
		this.emailDomainCount = 0;
		this.defaultDomainTreatment = 'include';
		// for multiselect model
		this.roles = ['Client', 'Member', 'Editor/Intel Leader', 'Reader'];
		// for multiselect items
		this.roleChoices = this.roles.map(roleName => {
			return {
				role: roleName == 'Editor/Intel Leader' ? 'intel leader' : roleName.toLowerCase(),
				label: roleName,
			};
		});
		// default to 30 days back
		this.defaultStart = moment().add(-30, 'days').format('YYYY-MM-DD');
		this.defaultEnd = moment().format('YYYY-MM-DD');
		if (document.documentElement.classList.contains('is-iframe')) {
			this.defaultHubIds = String(Identity.currentClient().id);
		} else {
			this.defaultHubIds = Identity.hubIdsInThisAccount().join(',') || 'account';
		}
		this.defaultColor = this.Theme.data().bubble;
		this.excluded_values = [];
		this.divisions = [];
		// self bind some callbacks
		this.runTrend = this.runTrend.bind(this);
		this.previewDocument = this.previewDocument.bind(this);
		this.downloadDocument = this.downloadDocument.bind(this);
		this.viewPost = this.viewPost.bind(this);
		// put url params into class properties
		this.readParamsFromUrl();
		// setup top nav
		this.breadcrumbs = this.getBreadcrumbs();
		this.updatePageTitle();
		// allowed values
		this.hubChoices = Identity.hubChoices();
		this.limitChoices = [
			{ id: 5, name: 'Top 5' },
			{ id: 10, name: 'Top 10' },
			{ id: 15, name: 'Top 15' },
			{ id: 20, name: 'Top 20' },
			{ id: 25, name: 'Top 25' },
		];
		this.isAnonymous = this.report.name === 'top-metric-post-secret-link-view';
		// load us up!
		this.initialLoad();
	}

	getUrlDefaults() {
		return {
			start: this.defaultStart,
			end: this.defaultEnd,
			graph: this.report.graph,
			limit: 10,
			filter_text: '',
			user_domain: '',
			user_domain_treatment: this.defaultDomainTreatment,
			roles: '',
			divisions: '',
			hubs: this.defaultHubIds,
			color: '',
			trend_of: '',
			trend_label: '',
			excluded_values: '',
			in_iframe: false,
		};
	}

	/**
	 * Validates and normalizes domains so just the domain remains;
	 * Can handle any item in the following input: 'example.com, user@example.com, user, user@example,'
	 * @param domains {String}  comma-separated list of domain names
	 * @returns {String}
	 */
	normalizeEmailDomains(domains) {
		// NOTE: this doesn't handle non-ascii TLDs
		const tldRegex = /[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
		let processedDomains = [];
		// split into individual items and clean up
		domains
			.split(',')
			.filter(Boolean)
			.forEach(domain => {
				domain = domain.trim();
				// skip if domain doesn't end in `.sometld`
				if (!tldRegex.test(domain)) {
					return;
				}
				const atLocation = domain.indexOf('@');
				// strip the @ if it has one
				if (atLocation === 0) {
					domain = domain.replace('@', '');
				}
				// strip the user name if it has one
				else if (atLocation > -1) {
					const [user, domainName] = domain.split('@');
					domain = domainName;
				}
				processedDomains.push(domain);
			});

		this.emailDomainCount = processedDomains.length;

		// put them all back together as a string
		return processedDomains.join(',');
	}

	/**
	 * Gets component's data and packages them into their string
	 * representations for use as URL GET params
	 */
	getParamsFromView() {
		const params = {
			start: this.dateRange[0],
			end: this.dateRange[1],
			limit: this.limit,
			filter_text: this.filter_text,
			user_domain: this.normalizeEmailDomains(this.user_domain),
			user_domain_treatment: this.user_domain_treatment,
			roles: this.roles.join(','),
			divisions: ensure.array(this.divisions).join(','),
			trend_of: this.trend_of,
			trend_label: this.trend_label,
			color: this.color,
			graph: this.graph,
			hubs: ensure.array(this.hubs).join(','),
			excluded_values: ensure.array(this.excluded_values).join(','),
			in_iframe: this.in_iframe,
		};
		return params;
	}

	/**
	 * Builds query string of filter params
	 * @param inputParams
	 * @returns {string}
	 */
	buildUrl(inputParams) {
		const def = this.getUrlDefaults();
		const outputParams = {};
		_.forOwn(inputParams, (value, field) => {
			if (def[field] === inputParams[field]) {
				return;
			}
			outputParams[field] = value;
		});
		const query = queryString(outputParams);
		const qm = query ? '?' : '';
		return `/hub#/analytics/reports/${this.report.name}${qm}${query}`;
	}

	readParamsFromUrl() {
		// get values from URL, falling back to defaults
		const def = this.getUrlDefaults();
		_.forOwn(def, (value, field) => {
			this[field] = this.$stateParams[field] || value;
		});
		// format items that are not strings
		this.limit = Number(this.limit);
		this.excluded_values = this.excluded_values.split(',').filter(Boolean);
		this.roles = this.roles.split(',').filter(Boolean);
		this.divisions = this.divisions.split(',').filter(Boolean);
		this.hubs = this.hubs.split(',').filter(Boolean).map(Number);
		// and datepicker needs a composite of start and end
		this.dateRange = [this.start, this.end];
	}

	async initialLoad() {
		try {
			this.isLoading = true;
			this.data = false;

			if (this.report.divisions) {
				this.showDivisionFilter = this.Cfg.readAsBool('UI.divisions_filter');
				const hideDivisionsForTheseReports = ['top-search-user', 'top-post-contributor'];
				if (hideDivisionsForTheseReports.includes(this.report.name) && this.trend_of) {
					this.showDivisionFilter = false;
				} else {
					// set up multiselect label
					await this.setupDivisions();
				}
			}
			const params = this.getParamsFromView();
			params.tz_offset = new Date().getTimezoneOffset();
			params.start = moment(params.start).startOf('day').format();
			if (params.start == 'Invalid date') {
				params.start = this.defaultStart;
			}
			params.end = moment(params.end).endOf('day').format();
			if (params.end == 'Invalid date') {
				params.end = this.defaultEnd;
			}
			// get Hubs header
			const headers = {
				Hubs: params.hubs,
			};
			// avoid sending some things to api
			params.trend_label = undefined;
			params.graph = undefined;
			params.color = undefined;
			params.hubs = undefined;
			params.in_iframe = undefined;

			const reportData = await this.Api.get(`/analytics/data/${this.report.name}`, params, headers);
			if (reportData.data) {
				this.isLoading = false;
				//431 is request header fields too large
				if (reportData.status === 431) {
					this.tooManyHubs = true;
					return;
				}
				this.data = ensure.array(reportData.data);
				this.data.forEach(d => {
					// bubbles() has a minimum bubble size and
					// it directly changes .value to handle the minimums
					// so we need to use a different property for the data table
					// that sits to the left of the bubble chart
					d.count = d.value;
				});
				this.renderGraph();
			}
		} catch (e) {
			console.log('error fetching data', e.message);
			this.isLoading = false;
		}
	}

	/**
	 * Fetches a list of user divisions and sets up the division filter label
	 */
	async setupDivisions() {
		// options used by the division multiselect
		this.divisionChoices = await this.fetchDivisionList();
		// trim any whitespace and make sure the user entered division label's first letter is capitalized
		let label = this.Cfg.read('UI.divisions_filter_label').trim();
		const upperCasedLabel = label.charAt(0).toUpperCase() + label.slice(1);
		this.divisionFilterLabel = `Filter by ${upperCasedLabel ? upperCasedLabel : 'Division'}`;
	}

	/**
	 * Fetch a list of divisions based on the set client ids
	 * @returns {Promise<*[]|*>} - array of objects if successful, empty array if error or no divisions found
	 */
	async fetchDivisionList() {
		try {
			// get Hubs header
			const headers = {
				Hubs: this.hubs,
			};
			const divisions = await this.Api.get('/analytics/divisions', {}, headers);
			return divisions;
		} catch (e) {
			return [];
		}
	}

	reload() {
		this.isLoading = true;
		const params = this.getParamsFromView();
		const url = this.buildUrl(params);
		window.location.href = url;
	}

	getBreadcrumbs() {
		const crumbs = [
			{ label: 'Admin', url: '/hub#/admin/users' },
			{ label: 'Analytics', url: '/hub#/analytics' },
		];
		const params = this.getParamsFromView();
		if (params.trend_of) {
			// back link should omit trend info and color
			params.trend_of = undefined;
			params.trend_label = undefined;
			params.color = undefined;
			crumbs.push({
				label: 'Report',
				url: this.buildUrl(params),
			});
			crumbs.push({
				label: 'Trend',
				url: null,
			});
		} else {
			crumbs.push({
				label: 'Report',
				url: null,
			});
		}
		return crumbs;
	}

	renderGraph() {
		if ((this.data && this.data.length === 0) || (this.trend_of && this.isTrendDataAllZeros())) {
			this.filtersTooNarrow = true;
		} else if (this.trend_of) {
			this.filtersTooNarrow = false;
			if (this.data.length > 19) {
				// labels too dense; hide every other label
				this.data.forEach((d, i) => {
					if (i % 2) {
						d.label = '';
					}
				});
			}
			// trend chart!
			barVertical({
				data: this.data,
				color: this.color,
				width: 1100,
				height: 450,
				withinElement: '#GraphArea',
			});
			this.renderTrendDetail();
		} else if (this.report.graph == 'bubble') {
			this.filtersTooNarrow = false;
			bubbles({
				data: this.data,
				width: 650,
				onClick: this.runTrend,
				withinElement: '#GraphArea',
			});
		} else if (this.report.graph == 'list') {
			this.filtersTooNarrow = false;
			barHorizontal({
				data: this.data,
				links: this.getListGraphLinks(),
				width: 1100,
				withinElement: '#GraphArea',
				onClick: this.runTrend,
			});
		} else if (this.report.graph == 'avatar') {
			this.filtersTooNarrow = false;
			this.data.sort((a, b) => b.value - a.value);
			this.isAvatar = true;
		}
	}

	isTrendDataAllZeros() {
		for (const row of this.data) {
			if (row.value > 0) {
				return false;
			}
		}
		return true;
	}

	runTrend(item) {
		this.isLoading = true;
		this.data = [];
		window.location.href = this.getTrendUrl(item);
	}

	getTrendUrl(record) {
		const params = this.getParamsFromView();
		params.trend_of = record.id;
		params.trend_label = record.label;
		params.color = record.color || this.defaultColor;
		return this.buildUrl(params);
	}

	renderTrendDetail() {
		const url = this.buildTrendDetailUrl();
		let hubs = ensure.array(this.hubs).join(',');
		if (this.report.trend_detail_type == 'posts') {
			if (this.report.name === 'top-post-hub') {
				hubs = [this.trend_of];
			}
			this.Api.get(url, {}, { Hubs: hubs }).then(
				posts => {
					this.trendDetailPosts = posts;
				},
				err => {
					console.log('err getting trend detail posts', err);
				}
			);
		} else if (this.report.trend_detail_type == 'events') {
			this.Api.get(url).then(
				events => {
					this.trendDetailEvents = events;
				},
				err => {
					console.log('err getting trend detail events', err);
				}
			);
		} else if (this.report.trend_detail_type == 'searches') {
			this.Api.get(url, {}, { Hubs: hubs }).then(
				resp => {
					this.trendDetailSearches = resp.data.map(record => {
						record.criteria = JSON.parse(record.criteria);
						record.label = 'Posts ' + record.criteria.label;
						return record;
					});
				},
				err => {
					console.log('err getting trend detail searches', err);
				}
			);
		} else if (this.report.trend_detail_type == 'users') {
			this.Api.get(url, {}, { Hubs: hubs }).then(
				users => {
					this.trendDetailUsers = users;
				},
				err => {
					console.log('err getting trend detail users', err);
				}
			);
		}
	}

	buildTrendDetailUrl() {
		const tpl = this.report.trend_detail_url;
		const params = this.getParamsFromView();
		params.start = moment(params.start).startOf('day').format();
		params.end = moment(params.end).endOf('day').format();
		const url = tpl.replace(/\{([^}]+)\}/g, ($0, $1) => {
			return encodeURIComponent(params[$1] || '');
		});
		return url;
	}

	getListGraphLinks() {
		if (this.report.name == 'top-metric-preview-document' || this.report.name == 'top-metric-download') {
			return [
				{ text: 'preview', onClick: this.previewDocument },
				{ text: 'download', onClick: this.downloadDocument },
				{ text: 'view trend', onClick: this.runTrend },
			];
		} else if (
			this.report.name == 'top-metric-click-source' ||
			this.report.name == 'top-metric-post-view' ||
			this.report.name == 'top-metric-post-secret-link-view' ||
			this.report.name == 'top-metric-post-private-link-view' ||
			this.report.name == 'top-post-likes' ||
			this.report.name == 'top-post-comments'
		) {
			return [
				{ text: 'view post', onClick: this.viewPost },
				{ text: 'view trend', onClick: this.runTrend },
			];
		} else if (this.report.name == 'top-post-hub' || this.report.name == 'top-metric-partner-link-click') {
			return [{ text: 'view trend', onClick: this.runTrend }];
		}
		return [];
	}

	previewDocument(item) {
		this.Att.popupId(item.id);
	}

	downloadDocument(item) {
		const urlParam = AuthTypes.getUrlParam('', this.storage);
		const url = `${window.BASE_URL}/api/v2/files/${item.id}/download?disposition=attachment&${urlParam}`;
		window.location.href = url;
	}

	viewPost(item) {
		this.modal.state('hub.PostDetail', {
			heading: 'Post Details',
			params: { id: item.id },
		});
	}

	exportPng() {
		const svg = document.querySelector('#GraphArea svg');
		const name = this.getReportName();
		saveSvgAsPng(svg, `${name}.png`);
	}

	getReportName() {
		// TODO: use a "title" returned with the report data
		return this.report.description;
	}

	updatePageTitle() {
		const parts = [this.report.type, this.report.details];
		if (this.$stateParams.trend_label) {
			parts.push(this.$stateParams.trend_label);
		}
		const title = parts.join(' / ');
		this.pageTitle = title;

		// we have to delay because of $title in the resolves
		setTimeout(() => {
			// Append the appname value to the title, unless it's empty, in which case just append "Sharpr".
			const appName = (this.Theme.data().appname || '').trim(); // NOTE: Avoiding the weird case of all whitespace.

			document.title = `Analytics / ${title} :: ` + (appName || 'Sharpr');
		}, 50);
	}

	exportExcel() {
		this.isExcelLoading = true;
		const params = {
			body: this.data.map(row => {
				// the bubbles() visualization changes row.value
				// we'll use .count which retains the original values
				// returned by the call to the analytics api
				// see initialLoad() for reference
				return [row.label, row.count];
			}),
			heading: this.report.table,
			title: this.pageTitle,
		};
		const endpoint = '/excel/create';
		this.Api.request('post', endpoint, params).then(
			result => {
				if (result.status < 200 || result.status > 299 || !result.data.hash) {
					this.modal.alert({ text: 'Error exporting to Excel' });
					return false;
				}

				this.downloadExcel(result.data.hash);
				return true;
			},
			err => {
				this.modal.alert({ text: 'Error requesting Excel export' });
				this.isExcelLoading = false;
			}
		);
	}

	downloadExcel(hash) {
		const endpoint = '/excel/download/' + hash;
		this.Api.request('get', endpoint + '.html')
			.then(
				result => {
					if (result.status < 200 || result.status > 299 || !result.data) {
						this.modal.alert({ text: 'Error downloading Excel file' });
						return false;
					}

					window.location.href = '/api/v2' + endpoint;
					return true;
				},
				() => {
					this.modal.alert({ text: 'Error starting Excel download' });
				}
			)
			.finally(() => {
				// give a bit for browser to start download
				this.$timeout(() => (this.isExcelLoading = false), 2000);
			});
	}

	toggleDomainBox() {
		const closing = this.domainsOpen;
		if (closing) {
			this.reload();
		}
		this.domainsOpen = !this.domainsOpen;
	}

	clearEmailDomainSelections() {
		const defaults = this.getUrlDefaults();
		if (this.user_domain != defaults.user_domain || this.user_domain_treatment != defaults.user_domain_treatment) {
			this.user_domain = defaults.user_domain;
			this.user_domain_treatment = defaults.user_domain_treatment;
			this.reload();
		}
		this.domainsOpen = false;
	}

	temporarilyHideItem(item) {
		this.excluded_values.push(item.label);
		this.reload();
	}

	permanentlyHideItem(item) {
		this.Api.post('/analytics/exclude_value', {
			value: item.label,
			report_name: this.report.name,
		}).then(() => {
			// this isn't required for hiding to work. however, `this.reload()` doesn't work unless a param has changed.
			this.excluded_values.push(item.label);
			this.reload();
		});
	}
}
